import React, { useState, useMemo, useEffect, useCallback } from 'react'
import { animated, useSpring, useSpringRef } from '@react-spring/web'
import {
	type SimulationLeaderboard,
	type Award,
} from '@mission.io/mission-toolkit'
import {
	SPECIAL_EFFECT_IDS as SOUNDS,
	AWARD_TYPES,
} from '@mission.io/mission-toolkit/constants'
import {
	useOnResultsEnd,
	useAnalyticsQuestionPercent,
	useMissionState,
	getTopScorers,
	useLocalDispatch,
	startLocalSoundEffect,
} from '../../../context'
import { PAGE_FADE_DURATION, BEFORE_FADE_OUT_DURATION } from './durations'
import { styled } from 'styled-components'
import type { SharedProps } from './types'
import { cssPlayerHeightUnit } from '../../../utility/constants'
import {
	useAnalyticsClassAwards,
	useLeaderboard,
} from '../../../context/provider'
import QuestionResults from './QuestionScores'
import TopScores from './TopScores'
import FinalScores from './ScoreData'
import Leaderboard from './Leaderboard'
import ConsolidatedDataView from './ConsolidatedDataView'
import { HealthAward } from './HealthAward'

type ShouldSkipArguments = {
	questionPercentage: ?number,
	topStudentCount: number,
	leaderboard: ?SimulationLeaderboard,
	awards: ?Array<Award>,
}

// the animation pages to play
const ANIMATIONS: {
	component: (prop: SharedProps) => React$Node, // the animated component to use as a child of the page
	shouldSkip?: (data: ShouldSkipArguments) => boolean, // if this animation should be played (if not defined, animation will always play)
}[] = [
	{
		component: QuestionResults,
		shouldSkip: ({ questionPercentage }) => questionPercentage == null,
	},
	{
		component: HealthAward,
		shouldSkip: ({ awards }) =>
			!awards?.some(award => award.type === AWARD_TYPES.CLASS_HEALTH),
	},
	{
		component: TopScores,
		shouldSkip: ({ topStudentCount }) => topStudentCount === 0,
	},
	{
		component: function LeaderBoardCoordinator({
			started,
			onFinish,
		}: SharedProps): React$Node {
			const [finishedScores, setFinishedScores] = useState(false)
			const onScoreFinish = useCallback(() => setFinishedScores(true), [])
			return (
				<>
					<FinalScores started={started} onFinish={onScoreFinish} />
					<Leaderboard started={finishedScores} onFinish={onFinish} />
				</>
			)
		},
		shouldSkip: ({ leaderboard }) => !leaderboard,
	},
	{
		component: ConsolidatedDataView,
	},
]

/**
 * EndOfMissionSequence - used to coordinate the end of mission sequence. Analytics must have been received
 *     before mounting this component
 *
 * @returns React$Node
 */
export default function EndOfMissionSequence(): React$Node {
	const [pageFadedIn, setPageFadedIn] = useState(false)
	const [pageShouldBeVisible, setPageShouldBeVisible] = useState(true)

	const onResultEnd = useOnResultsEnd()

	const missionState = useMissionState()

	const topStudentCount = useMemo(
		() => getTopScorers(missionState).length,
		[missionState]
	)
	const questionPercentage = useAnalyticsQuestionPercent()
	const leaderboard = useLeaderboard().leaderboard
	const awards = useAnalyticsClassAwards()

	/**
	 * getNextAnimationToPlay - get the index of the next animation to play
	 *
	 * @param  {number=-1} currentAnimation - the current playing animation index, (if not passed in, will find the starting animation)
	 *
	 * @returns number - the next animation to play
	 */
	const getNextAnimationToPlay = useCallback(
		(currentAnimation: number = -1): number => {
			let nextAnimation = currentAnimation
			for (let i = currentAnimation + 1; i < ANIMATIONS.length; i++) {
				nextAnimation = i
				const animation = ANIMATIONS[i]
				if (
					animation.shouldSkip?.({
						questionPercentage,
						topStudentCount,
						leaderboard,
						awards,
					})
				) {
					continue
				}
				break
			}
			return nextAnimation
		},
		[questionPercentage, topStudentCount, leaderboard, awards]
	)

	const [animationIndex, setAnimationIndex] = useState(() =>
		getNextAnimationToPlay()
	)

	const onPageFadeInFinish = useCallback(() => setPageFadedIn(true), [])

	const onAnimationFinish = useCallback(() => {
		if (getNextAnimationToPlay(animationIndex) !== animationIndex) {
			setPageShouldBeVisible(false)
		} else {
			onResultEnd()
		}
	}, [getNextAnimationToPlay, animationIndex, onResultEnd])

	const onPageFadeOutFinish = useCallback(() => {
		setAnimationIndex(getNextAnimationToPlay(animationIndex))
		setPageFadedIn(false)
		setPageShouldBeVisible(true)
	}, [animationIndex, getNextAnimationToPlay])

	const Component = ANIMATIONS[animationIndex]?.component || NoAnimation

	return (
		<Page
			onFadeInFinish={onPageFadeInFinish}
			onFadeOutFinish={onPageFadeOutFinish}
			shouldBeVisible={pageShouldBeVisible}>
			<Component started={pageFadedIn} onFinish={onAnimationFinish} />
		</Page>
	)
}
/**
 * Page - A react component which positions other animated components on the screen and fades in and out.
 *
 * @param  {Object} props - the react props
 * @param  {React$Node} props.children - the children of this component
 * @param  {boolean} props.shouldBeVisible - true if the component should be visible, false otherwise (changing this prop will cause fade in/out effects)
 * @param  {() => void} props.onFadeInFinish - a callback when the fade in animation is completed
 * @param  {() => void} props.onFadeOutFinish - a callback when the fade out animation is completed
 *
 * @returns React$Node
 */
function Page({
	children,
	shouldBeVisible,
	onFadeInFinish,
	onFadeOutFinish,
}: {
	children: React$Node,
	shouldBeVisible: boolean,
	onFadeInFinish: () => void,
	onFadeOutFinish: () => void,
}): React$Node {
	const screenFadeRef = useSpringRef()
	const fadeStyles = useSpring({
		ref: screenFadeRef,
	})
	const dispatch = useLocalDispatch()

	useEffect(() => {
		if (shouldBeVisible) {
			screenFadeRef.start({
				from: { opacity: 0 },
				to: { opacity: 1 },
				config: { duration: PAGE_FADE_DURATION },
				onRest: onFadeInFinish,
				onStart: () => {
					dispatch(startLocalSoundEffect(SOUNDS.EOMS_SWIPE_HARD))
				},
			})
		} else {
			screenFadeRef.start({
				delay: BEFORE_FADE_OUT_DURATION,
				from: { opacity: 1 },
				to: { opacity: 0 },
				config: { duration: PAGE_FADE_DURATION },
				onRest: onFadeOutFinish,
			})
		}
	}, [
		onFadeInFinish,
		onFadeOutFinish,
		screenFadeRef,
		shouldBeVisible,
		dispatch,
	])

	return <Centered style={fadeStyles}>{children}</Centered>
}

/**
 * NoAnimation - a component to render when no animations are available
 *
 * @param  {SharedProps} {onFinish} - a callback to try to get a new animation provided by the Frame
 *
 * @returns React$Node
 */
function NoAnimation({ onFinish }: SharedProps): React$Node {
	useEffect(() => onFinish(), [onFinish])
	return (
		<StyledError>
			<div>No Results</div>
		</StyledError>
	)
}

const Centered = styled(animated.div)`
	display: flex;
	flex-direction: column;
	align-items: center;
	height: 100%;
	color: white;
`

const StyledError = styled.div`
	font-size: ${cssPlayerHeightUnit(20)};
	height: 100%;
	width: 100%;
	display: flex;
	align-items: center;
	justify-content: center;
`
